// Shared inline rich-text helpers for text blocks (heading / paragraph /
// quote). Authors may apply inline formatting (bold, italic, underline,
// strikethrough, link, inline code, text color) which the designer stores
// as `block.html`; the plain-text `block.text` is always kept in sync as a
// fallback. Every render surface (designer canvas, RuntimeBlockHost,
// runtime/client BlockHost, site SSR) resolves the pair through
// `inlineRichHtml` so output stays identical across surfaces.

import { substituteVars, type VarSource } from './actions/substitute'

// Strip <script> elements, inline on*= handlers, and javascript: URLs.
// Same security bar already applied to notepad / custom_html via
// `stripScripts` in renderers.tsx — kept in lock-step here so inline rich
// text can never smuggle executable markup into a render surface.
export function sanitizeInlineHtml(html: string): string {
  return (html || '')
    .replace(/<script[\s\S]*?<\/script>/gi, '')
    .replace(/<\/?(?:iframe|object|embed|style|link|meta)[^>]*>/gi, '')
    .replace(/\son\w+\s*=\s*"[^"]*"/gi, '')
    .replace(/\son\w+\s*=\s*'[^']*'/gi, '')
    .replace(/\son\w+\s*=\s*[^\s>]+/gi, '')
    .replace(/javascript:/gi, '')
}

export function escapeHtml(s: string): string {
  return (s || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')
}

export function hasRichHtml(html?: string): boolean {
  // Treat an `html` value that carries no actual tags (just escaped/plain
  // text the contenteditable produced) as "not rich" so we don't lose the
  // plain-text rendering path's newline handling unnecessarily. Any tag
  // (formatting or <br>) flips this on.
  return !!(html && /<[a-z!/][\s\S]*>/i.test(html))
}

// Resolve a text/html block pair to display HTML:
//   • prefer rich `html` (var-substituted, then sanitized)
//   • otherwise escape plain `text` (var-substituted) and convert
//     newlines to <br> so multi-line paragraphs render as authored.
// Always returns a string safe to pass to dangerouslySetInnerHTML.
export function inlineRichHtml(opts: { text?: string; html?: string; vars?: VarSource }): string {
  const { text, html, vars } = opts
  const src: VarSource = vars ?? ({} as VarSource)
  if (hasRichHtml(html)) return sanitizeInlineHtml(substituteVars(html as string, src))
  return escapeHtml(substituteVars(text ?? '', src)).replace(/\n/g, '<br>')
}
